/**
 * Timing of the playout queue for the WHEP video receiver. No browser APIs, so it runs in the worker and in tests.
 *
 * Each frame is due at rtpMs + base + delay. base is the smallest arrival-minus-RTP offset in the window, and delay
 * is the largest lateness in the window plus a margin, so frames leave on the sender's cadence instead of in
 * network bursts. delay rises at once and falls slowly.
 *
 * The delay never passes maxDelayMs. A frame later than that is released at once and does not raise the delay.
 * That limit is the difference between the two modes below: after a network stall, a viewer sees the missed
 * video replayed and a pilot is taken straight back to live.
 */

export interface PlayoutConfig {
  /** Longest time the queue holds a frame. A frame later than this is released at once. */
  maxDelayMs: number;
  /** Delay before the first frames have been measured. */
  startDelayMs: number;
  /** Added to the largest lateness in the window. */
  marginMs: number;
  /** How fast the delay falls. 100 plays about 10% fast while it drains. */
  downMsPerS: number;
  /** How far back lateness is remembered. */
  windowMs: number;
  /** Frames later than maxDelayMs for this long mean the timing moved for good, and the window starts over. */
  rebaseAfterMs: number;
}

const SHARED = {
  startDelayMs: 200,
  marginMs: 20,
  downMsPerS: 100,
  windowMs: 10000,
  rebaseAfterMs: 2000
};

/**
 * For people who examine the scene. Video missed in a stall of up to 10 s is replayed about 10% fast, so nothing
 * is skipped; the picture can be up to 10 s behind live while it catches up.
 */
export const PLAYOUT_VIEWER: PlayoutConfig = { ...SHARED, maxDelayMs: 10000 };

/** For flying. The picture is never more than half a second behind; video missed in a stall flashes past. */
export const PLAYOUT_PILOT: PlayoutConfig = { ...SHARED, maxDelayMs: 500 };

/** Every stream is a viewer stream today. Pass PLAYOUT_PILOT where someone flies from the picture. */
export const DEFAULT_PLAYOUT_CONFIG: PlayoutConfig = PLAYOUT_VIEWER;

/** No added delay: every frame leaves as it arrives. Keeps the frame filter available without the queue. */
export const PLAYOUT_PASSTHROUGH: PlayoutConfig = { ...SHARED, maxDelayMs: 0 };

/**
 * What a screen passes down as `playout`: timing overrides, plus `strict`, which keeps frames that break the H.264
 * reference chain away from the decoder so the picture holds instead of smearing (on unless set to false).
 * `false` plays without the worker altogether.
 */
export type PlayoutOptions = Partial<PlayoutConfig> & { strict?: boolean };

/**
 * The crew camera settings as playout options: Video Playback Auto/Actual is the queue, Frame Filter Strict/Off
 * is the chain check. With both off there is nothing for the worker to do.
 */
export function crewPlayout(autoPlayout: boolean, strictFrames: boolean): PlayoutOptions | false {
  if (!autoPlayout && !strictFrames) {
    return false;
  }
  return autoPlayout ? { strict: strictFrames } : { maxDelayMs: 0, strict: strictFrames };
}

/** The defaults with every valid override applied. A value that is not a finite number of zero or more is ignored. */
export function resolvePlayoutConfig(overrides?: Partial<PlayoutConfig>): PlayoutConfig {
  const config = { ...DEFAULT_PLAYOUT_CONFIG };
  if (overrides) {
    (Object.keys(config) as (keyof PlayoutConfig)[]).forEach((key) => {
      const value = overrides[key];
      if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        config[key] = value;
      }
    });
  }
  return config;
}

export class PlayoutScheduler {
  private readonly config: PlayoutConfig;
  private offs: { t: number; off: number }[] = [];
  private delay: number;
  private lastRtpMs: number | null = null;
  private lateSince: number | null = null;

  /** startDelayMs replaces the configured start delay, for a reconnect that resumes from the delay it had learned. */
  constructor(config: PlayoutConfig, startDelayMs?: number) {
    this.config = config;
    const start =
      typeof startDelayMs === 'number' && Number.isFinite(startDelayMs) && startDelayMs >= 0
        ? startDelayMs
        : config.startDelayMs;
    this.delay = Math.min(start, config.maxDelayMs);
  }

  get delayMs(): number {
    return this.delay;
  }

  /** Largest lateness in the window, for logs. */
  get spreadMs(): number {
    if (this.offs.length === 0) {
      return 0;
    }
    const offs = this.offs.map((o) => o.off);
    return Math.max(...offs) - Math.min(...offs);
  }

  /**
   * The time to release a frame that arrived at arrivalMs with RTP time rtpMs, both in milliseconds. The result is
   * never before arrivalMs and never more than maxDelayMs after it.
   */
  schedule(arrivalMs: number, rtpMs: number | null): number {
    const { maxDelayMs, marginMs, downMsPerS, windowMs, rebaseAfterMs } = this.config;
    if (rtpMs === null) {
      return arrivalMs;
    }
    const off = arrivalMs - rtpMs;
    this.offs = this.offs.filter((o) => o.t >= arrivalMs - windowMs);

    if (this.offs.length > 0) {
      const late = off - Math.min(...this.offs.map((o) => o.off));
      if (late < -maxDelayMs) {
        // The RTP clock jumped, or a long delay ended: the old offsets say nothing about this frame.
        this.offs = [];
      } else if (late > maxDelayMs) {
        if (this.lateSince === null) {
          this.lateSince = arrivalMs;
        }
        if (arrivalMs - this.lateSince < rebaseAfterMs) {
          return arrivalMs;
        }
        this.offs = [];
      }
    }
    this.lateSince = null;

    this.offs.push({ t: arrivalMs, off });
    const offs = this.offs.map((o) => o.off);
    const base = Math.min(...offs);
    const target = Math.min(maxDelayMs, Math.ceil(Math.max(...offs) - base + marginMs));
    if (target >= this.delay) {
      this.delay = target;
    } else if (this.lastRtpMs !== null) {
      // Media time, not arrival time: arrivals come in bursts, and a burst would drop the delay in one visible step.
      const elapsedMs = Math.min(Math.max(0, rtpMs - this.lastRtpMs), 1000);
      this.delay = Math.max(target, this.delay - (downMsPerS * elapsedMs) / 1000);
    }
    this.lastRtpMs = rtpMs;

    return Math.min(Math.max(arrivalMs, rtpMs + base + this.delay), arrivalMs + maxDelayMs);
  }
}
